import React, { createContext, useContext } from 'react';
import { useSnapshot } from 'valtio';

import Slider from '@cartwhl/ui/atoms/Slider';
import Text from '@cartwhl/ui/base/Text';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import GoButton from '@cartwhl/ui/input/GoButton';
import TrimBarComponent from '@cartwhl/ui/organisms/TrimBar';

import { AnimationPlayerStoreInstance } from 'stores/animationPlayerStoreFactory';

const AnimationPlayerStoreContext = createContext<
  AnimationPlayerStoreInstance | undefined
>(undefined);

const useAnimationPlayerStore = () => {
  const context = useContext(AnimationPlayerStoreContext);
  if (!context) {
    throw new Error(
      'useAnimationPlayerStore must be used within AnimationPlayerStoreContext.Provider',
    );
  }
  return context;
};

const Root = ({
  children,
  animationPlayerStore,
}: {
  children: React.ReactNode;
  animationPlayerStore: AnimationPlayerStoreInstance;
}) => {
  return (
    <AnimationPlayerStoreContext.Provider value={animationPlayerStore}>
      <div className="pointer-events-auto flex items-center gap-2">
        {children}
      </div>
    </AnimationPlayerStoreContext.Provider>
  );
};

const Timeline = () => {
  const animationPlayerStore = useAnimationPlayerStore();
  const { currentFrame, duration, disabled } = useSnapshot(
    animationPlayerStore.state,
    { sync: true },
  );

  return (
    <div className="pointer-events-auto flex h-10 w-full items-center px-2 lg:px-0">
      <Slider
        currentValue={formatFrames(currentFrame)}
        min={formatFrames(0)}
        max={formatFrames(duration.frames - 1)}
        size="sm"
        step={1}
        onChange={(value) => {
          animationPlayerStore.setFrame(parseSliderValue(value));
        }}
        trackColor={'player'}
        rangeColor={'player'}
        disabled={disabled}
      />
    </div>
  );
};

const FrameCount = () => {
  const animationPlayerStore = useAnimationPlayerStore();
  const { currentFrame, duration } = useSnapshot(animationPlayerStore.state, {
    sync: true,
  });
  return (
    <div className="text-gray-90 relative flex h-full items-center">
      <Text
        as="div"
        variant="studio/body/medium/sm"
        className="text-gray-90 text-end opacity-60"
        style={{
          width: duration.frames.toString().length * 21,
        }}
      >
        {!!duration.frames && (
          <>
            {currentFrame + 1}/{duration.frames}
          </>
        )}
      </Text>
    </div>
  );
};

const PlayButton = () => {
  const animationPlayerStore = useAnimationPlayerStore();
  const { isPlaying } = useSnapshot(animationPlayerStore.state);
  return (
    <GoButton
      icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
      onClick={() => animationPlayerStore.playOrPause()}
    />
  );
};

const TrimBar = ({
  frameRate,
  minFrames,
  hexColor = '#42aad7',
}: {
  frameRate: number;
  minFrames?: number;
  hexColor?: string;
}) => {
  const animationPlayerStore = useAnimationPlayerStore();
  const { currentFrame, duration, trimFrameStart, trimFrameEnd } = useSnapshot(
    animationPlayerStore.state,
    {
      sync: true,
    },
  );
  const originalFps = duration.originalFrames / duration.originalSeconds;
  const convertToOriginalFrame = (fpsAdjustedFrame: number) => {
    return Math.round((fpsAdjustedFrame / frameRate) * originalFps);
  };
  const convertToFpsAdjustedFrame = (originalFrame: number) => {
    return Math.round((originalFrame / originalFps) * frameRate);
  };

  return (
    <div className="relative w-full">
      {/* <div className="absolute -top-10 right-0 left-0 h-10 text-[12px]">
        frameRate: {frameRate} <br />
        Trim start: {trimFrameStart ?? '-'} | Trim end: {trimFrameEnd ?? '-'} |
        Total frames: {duration.originalFrames} | Current frame: {currentFrame}
        <br />
      </div> */}
      <TrimBarComponent
        value={convertToOriginalFrame(currentFrame)}
        onValueChange={(frame: number) => {
          animationPlayerStore.setFrame(convertToFpsAdjustedFrame(frame));
        }}
        minGap={minFrames}
        trimStart={trimFrameStart ?? 0}
        trimEnd={trimFrameEnd ?? duration.originalFrames}
        onTrimChange={(s, e) => animationPlayerStore.setTrimFrames([s, e])}
        maxValue={duration.originalFrames}
        format={(frame) => `${Math.round(frame)}`}
        bgColor={hexColor}
        hideHandleInfo
      />
    </div>
  );
};

export default { Root, Timeline, FrameCount, PlayButton, TrimBar };

// We need to keep in mind that our system handles frames with "index zero"
// But the user should see "index one"
// The following functions seem trivial but they will help developers understand better what happens
// For sanity you can remove `+ 1` and `- 1` and see the actual numbers in the UI
function formatFrames(value: number) {
  return value + 1;
}
function parseSliderValue(value: number) {
  return value - 1;
}
